import { SelectionModel } from '@angular/cdk/collections';
import {
    ChangeDetectorRef,
    Component,
    Directive,
    EventEmitter,
    Input, OnChanges,
    OnInit,
    Optional,
    Output, SimpleChanges,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatColumnDef, MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, filter } from 'rxjs';
import { delay } from 'rxjs/operators';
import { ColumnMap } from '../../../../models/entities/column-map';

export interface TableCheckboxRowSelection {
    ids: number[];
    count: number;
}

export interface TableCheckboxRowSelectionEvent {
    id: number | 'all';
    state: 'add' | 'remove';
    selected: number;
    total: number;
}

@Component({
    selector: 'app-table-column-checkbox',
    template: `
        <ng-container matColumnDef="select">
            <mat-header-cell *matHeaderCellDef>
                <mat-checkbox class="column-checkbox-select-all"
                              [class.indeterminate-checkbox]="selectedCount > 0 && selectedCount < selectedTotal"
                              [checked]="selectedTotal > 0 && selectedCount === selectedTotal"
                              [indeterminate]="selectedCount > 0 && selectedCount < selectedTotal"
                              [disabled]="isLoading"
                              [disableRipple]="true"
                              (click)="setRowSelection($event, 'all', selectedCount === selectedTotal)">
                </mat-checkbox>
            </mat-header-cell>
            <mat-cell *matCellDef="let row">
                <mat-checkbox [checked]="selectedRows.isSelected(row.id) && !isLoading"
                              [disabled]="isLoading || isRowDisabled(row)"
                              [disableRipple]="true"
                              (click)="setRowSelection($event, row.id, selectedRows.isSelected(row.id))">
                </mat-checkbox>
            </mat-cell>
        </ng-container>
    `,
    standalone: true,
    imports: [
        MatTableModule,
        MatCheckboxModule,
    ],
})
export class TableColumnCheckboxComponent<T> implements OnInit {
    @Input() selectedCount = 0;
    @Input() selectedTotal = 0;
    @Input() selectedRows = new SelectionModel<number>(true, []);
    @Input() showSelectAll = false;
    @Input() isLoading = false;

    /** Rows this predicate answers true for cannot be ticked, and select-all skips them. */
    @Input() rowDisabledFn: (row: T) => boolean;

    @Input() columnEvent$: BehaviorSubject<{
        selection: TableCheckboxRowSelectionEvent;
        working: boolean;
    }>;

    @ViewChild(MatColumnDef, { static: true }) columnDef!: MatColumnDef;

    constructor(
        @Optional() public matTable: MatTable<T>,
        private _cd: ChangeDetectorRef,
    ) {
    }

    isRowDisabled(row: T): boolean {
        return !!this.rowDisabledFn?.(row);
    }

    ngOnInit(): void {
        if (this.matTable) {
            this._cd.detectChanges();
            this.matTable.addColumnDef(this.columnDef);
        }
    }

    /** Handles checkbox selection for a single row id or 'all'. */
    setRowSelection(event: MouseEvent, id: number | 'all', checked: boolean) {
        event.stopPropagation();

        if (this.columnEvent$?.value?.working) {
            return false;
        }

        this.columnEvent$.next({
            selection: {
                id,
                selected: this.selectedCount,
                total: this.selectedTotal,
                state: checked ? 'remove' : 'add',
            },
            working: true,
        });
    }
}

@UntilDestroy()
@Directive({
    selector: 'mat-table[tableCheckboxColumnDef]',
    standalone: true,
})
export class TableCheckboxColumnDefDirective<T> implements OnInit, OnChanges {
    @Input() tableCheckboxColumnDef!: ColumnMap[];
    @Input() tableCheckboxEnabled = false;
    @Input() tableInitialLoad = false;
    @Input() tableData: T[] = [];
    @Input() tableCheckboxRowDisabled: (row: T) => boolean;
    @Output() tableCheckboxRowChange = new EventEmitter<TableCheckboxRowSelection>();

    instance: TableColumnCheckboxComponent<T>;

    constructor(
        private _vcr: ViewContainerRef,
        private _matTable: MatTable<T>,
    ) {
    }

    /** Rows a caller has locked out of selection (e.g. Stripe-owned transactions). */
    private isSelectable(row: T): boolean {
        return !row || !this.tableCheckboxRowDisabled?.(row);
    }

    ngOnChanges({ tableInitialLoad, tableData, tableCheckboxRowDisabled }: SimpleChanges) {
        if (tableInitialLoad && this.instance) {
            this.instance.showSelectAll = !tableInitialLoad.currentValue;
            this.instance.isLoading = tableInitialLoad.currentValue;
        }

        if (tableCheckboxRowDisabled && this.instance) {
            this.instance.rowDisabledFn = this.tableCheckboxRowDisabled;
        }

        if (tableData && this.instance) {
            // Handle both T[] and MatTableDataSource<T>
            const rawData = tableData.currentValue;
            const currentData: T[] = Array.isArray(rawData) ? rawData : (rawData?.data || []);
            const realItems = currentData.filter((item: any) => !item?.isSkeleton && this.isSelectable(item));
            const hasSkeletons = currentData.some((item: any) => item?.isSkeleton);

            this.instance.selectedTotal = realItems.length;
            this.instance.isLoading = hasSkeletons || this.tableInitialLoad;
        }
    }

    ngOnInit() {
        if (!this.tableCheckboxEnabled) {
            return;
        }

        const componentRef = this._vcr.createComponent(TableColumnCheckboxComponent);

        this.instance = componentRef.instance as TableColumnCheckboxComponent<T>;
        this.instance.showSelectAll = !this.tableInitialLoad;
        this.instance.isLoading = this.tableInitialLoad;
        this.instance.rowDisabledFn = this.tableCheckboxRowDisabled;
        this.instance.columnEvent$ = new BehaviorSubject<{
            selection: TableCheckboxRowSelectionEvent;
            working: boolean;
        }>({
            selection: null,
            working: false,
        });

        // Listen for changes in the selection and update the selectedCount
        this.instance.columnEvent$.pipe(
            filter((selection) => selection.working),
            delay(10),
            untilDestroyed(this),
        ).subscribe({
            next: (event) => {
                const id = event.selection.id;
                // Get data from tableData input (handles both T[] and MatTableDataSource<T>)
                const currentData: T[] = Array.isArray(this.tableData) ? this.tableData : (this.tableData as any)?.data || [];

                if (id === 'all') {
                    // use count to update visual checkboxes, then data
                    this.instance.selectedCount = this.instance.selectedCount < this.instance.selectedTotal
                        ? this.instance.selectedTotal : 0;
                    if (this.instance.selectedCount === 0) {
                        this.instance.selectedRows.clear();
                    } else {
                        for (const row of currentData) {
                            if (!(row as any)?.isSkeleton && this.isSelectable(row)) {
                                this.instance.selectedRows.select(row['id']);
                            }
                        }
                    }
                } else if (this.isSelectable(currentData.find((row) => row['id'] === id))) {
                    if (this.instance.selectedRows.isSelected(id)) {
                        this.instance.selectedCount--;
                        this.instance.selectedRows.deselect(id);
                    } else {
                        this.instance.selectedCount++;
                        this.instance.selectedRows.select(id);
                    }
                }

                this.tableCheckboxRowChange.emit({
                    ids: this.instance.selectedRows.selected,
                    count: this.instance.selectedCount,
                });

                this.instance.columnEvent$.next({
                    selection: null,
                    working: false,
                });
            }
        });

        // Add the column definition to the table
        this._matTable.addColumnDef(this.instance.columnDef);

        // Insert 'select' at the beginning of the displayed columns array
        this.tableCheckboxColumnDef.unshift({
            key: 'select', title: 'Select', visible: true, isMobile: true, disabled: () => true
        });
    }
}
